import { createStyles, withStyles } from '@mui/styles';
import React, { Component } from 'react';
import { GridSortModel, GridSortDirection, GridSortItem, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Theme, Grid, Paper, Container } from '@mui/material';
import UrlConstants from '../../../constants/UrlConstants';
import { RootState } from '../../../setup';
import Pagination from '../../../interfaces/common/pagination';
import Paging from '../../../interfaces/common/paging';
import IMenuItem from '../../../interfaces/common/menuItem';
import MainLayout from '../../common/widgets/mainLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FactorSetsFiltersComponent from '../factorSetsFiltersComponent';
import { fetchFactorSetPriorities, getFactorSetPriorities, isLoadingFactorSetPriorities } from '../../../reducers/factorSetPriorityReducer';
import PriorityFilters from './priorityFilter';
import FactorSetOut from '../../../interfaces/output/factorSetOut';
import PriorityList from './priorityList';
import LanguageUtils from '../../../utils/LanguageUtils';
import QuickSearchToolbar from '../../common/quickSearchToolbar';
import PageUtils from '../../../utils/pageUtils';

interface IFactorSetPrioritiesProps {
    factorSetPriorities: Pagination<FactorSetOut>;
    classes: any;
    fetchFactorSetPriorities: any;
    isLoading: boolean;
    onChange: () => void;
}

interface IFactorSetPrioritiesState {
    searchTerm: string;
    paging: Paging;
    menuItem: IMenuItem;
    sortModel: GridSortModel;
    filters: PriorityFilters;
    columnVisibilityModel: GridColumnVisibilityModel;
}

const messages = {
    factorSetPriorities: LanguageUtils.createMessage('Factor set priorities'),
    add: LanguageUtils.createMessage('Add')
};

const styles = (theme: Theme) => createStyles({
    container: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    paper: {
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column'
    }
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchFactorSetPriorities: (paging: Paging, filters: PriorityFilters) => dispatch(fetchFactorSetPriorities({
        paging,
        filters
    }))
});

const mapStoreToProps = (store: RootState) => {
    return {
        factorSetPriorities: getFactorSetPriorities(store),
        isLoading: isLoadingFactorSetPriorities(store)
    };
};

class FactorSetPriorities extends Component<IFactorSetPrioritiesProps, IFactorSetPrioritiesState> {
    priorityListRef: any;
    constructor(props: IFactorSetPrioritiesProps) {
        super(props);
        this.priorityListRef = React.createRef();

        this.state = {
            searchTerm: '',
            paging: PageUtils.getDefaultPaging(),
            filters: {
                name: ''
                // default: true
            } as PriorityFilters,
            menuItem: {
                id: 1,
                name: messages.factorSetPriorities,
                icon: <FontAwesomeIcon icon="flag" size="1x" transform="grow-7" />,
                isSelected: false
            },
            sortModel: [{
                field: '',
                sort: '' as GridSortDirection
            } as GridSortItem],
            columnVisibilityModel: {}
        };
    }

    componentDidMount() {
        const { fetchFactorSetPriorities } = this.props;
        const { paging, filters } = this.state;

        fetchFactorSetPriorities(paging, filters);
    }

    onChange = (attribute: string, value: any) => {
        const { filters, paging } = this.state;

        const newFilter = { ...filters } as PriorityFilters;
        (newFilter as any)[attribute] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination
        });
    }

    applyFilters = () => {
        const { fetchFactorSetPriorities } = this.props;
        const { paging, filters } = this.state;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };
        this.setState({ paging: newPagination });

        fetchFactorSetPriorities(newPagination, filters);

        if(this.priorityListRef !== null && this.priorityListRef !== undefined &&
            this.priorityListRef.current !== null && this.priorityListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.priorityListRef.current.resetDataGridPage();
        }

    }

    setDefaultState = () => {
        const { fetchFactorSetPriorities } = this.props;
        const { paging } = this.state;

        const newSortModel = [{
            field: '',
            sort: '' as GridSortDirection
        } as GridSortItem] as GridSortModel;

        const newFilters = { name: '' } as PriorityFilters;
        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page,
            sort: newSortModel
        };

        this.setState({
            paging: newPagination,
            filters: newFilters,
            searchTerm: ''
        });

        fetchFactorSetPriorities(newPagination, newFilters);

        if(this.priorityListRef !== null && this.priorityListRef !== undefined &&
            this.priorityListRef.current !== null && this.priorityListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.priorityListRef.current.resetDataGridPage();
        }
    }

    onChangeQuickSearch = (value: any) => {
        const { filters, paging } = this.state;
        const { fetchFactorSetPriorities } = this.props;

        const newFilter = { ...filters } as PriorityFilters;
        (newFilter as any)['name'] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination,
            searchTerm: value
        });

        fetchFactorSetPriorities(newPagination, newFilter);
    }

    _onSortChange = (newModel: GridSortModel) => {
        const { fetchFactorSetPriorities } = this.props;
        const { sortModel, filters, paging } = this.state;

        if(JSON.stringify(sortModel) !== JSON.stringify(newModel)) {
            const newPagination = {
                ...paging,
                sort: newModel
            };
            this.setState({
                sortModel: newModel,
                paging: newPagination
            });
            fetchFactorSetPriorities(newPagination, filters);
        }
    }

    render() {
        const { classes, factorSetPriorities, fetchFactorSetPriorities, isLoading } = this.props;
        const { searchTerm, paging, filters, menuItem, columnVisibilityModel } = this.state;

        return (
            <MainLayout menuItem={menuItem}>
                <Container maxWidth="xl" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <QuickSearchToolbar
                                    onSearch={this.onChangeQuickSearch}
                                    searchTerm={searchTerm}
                                    includeFilters={true}
                                    actions={[{
                                        text: messages.add,
                                        icon: 'plus',
                                        href: `${UrlConstants.FACTOR_SETS}/priority/add`
                                    }]}
                                >
                                    <FactorSetsFiltersComponent applyFilters={this.applyFilters} setDefaultState={this.setDefaultState} filters={filters} onChange={this.onChange} />
                                </QuickSearchToolbar>
                                <PriorityList rows={factorSetPriorities} isLoading={isLoading}
                                    ref={this.priorityListRef}
                                    onPageChange={(nextPage: number, nextSize: number) => {
                                        const newPage = {
                                            ...paging,
                                            page: nextPage,
                                            size: nextSize
                                        };
                                        fetchFactorSetPriorities(newPage, filters);
                                        this.setState({ paging: newPage });
                                    }}
                                    onSortModelChange={(sortModel: GridSortModel) => this._onSortChange(sortModel)}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={(newModel : GridColumnVisibilityModel) =>
                                        this.setState({
                                            columnVisibilityModel: newModel
                                        })
                                    }
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>

            </MainLayout>
        );
    }
}

export default withStyles(styles)(connect(mapStoreToProps, mapDispatchToProps)(FactorSetPriorities));